import { Action, createReducer, on } from '@ngrx/store';
import * as FormActions from './static-form.actions';
import { CompletedStaticForm } from 'Shared/app/models/completedStaticForm';
import { FormType } from 'Shared/app/enums/FormType';

/*
    #pages = new BehaviorSubject<any[]>([]);
    #currentPage = new BehaviorSubject(1);
    #pageCount = new BehaviorSubject(1);
    #visiblePageCount = new BehaviorSubject(1);
*/

export interface StaticFormState {
  errorCount: number;
  isEditMode: boolean;
  isLoading: boolean;
  typeName: string;
  formType: FormType | null;
  isPostRegister: boolean;
  pages: unknown[];
  pageCountTotal: number;
  visiblePages: number[];
  currentPage: number;
  validationRequest: boolean;
  completedForm: CompletedStaticForm | null;
  error: Error;
  hasChanges: { hasChanges: boolean };
  paginationVisible: boolean;
  paginationPreviousPageVisible: boolean;
  showAllPages: boolean;
  lastValidatedPage: number;
  shouldStopBeforeSaving: boolean;
  stoppedBeforeSaving: boolean;
  shouldForceSave: boolean;
  onNextPage: (currentPage: number) => void;
  onPreviousPage: (currentPage: number) => void;
  additionalQuestionsToConvert: unknown[];
  shouldStopBeforeDrafting: boolean;
  stoppedBeforeDrafting: boolean;
  shouldForceDraft: boolean;
  shouldStopBeforeUpdating: boolean;
  stoppedBeforeUpdating: boolean;
  shouldForceUpdate: boolean;
  isPerformView: boolean;
  isCareProvider: boolean;
  questionsToValidate: string[] | null;
  isPrinting: boolean;
  shouldStopBeforeNextPage: boolean;
  nextPageButtonClicked: boolean;
  saveButtonClicked: boolean;
  saveDraftCompleted: { isCompleted: boolean };
  saveDraftFailed: { isFailed: boolean };
  shouldFocusFormName: { shouldFocus: boolean; shouldReadInstructions: boolean };
  journalizationDisabled: { isDisabled: boolean };
  nextPageLabel: string;
  customErrorMessages: string[];
}

const initialState: StaticFormState = {
  errorCount: 0,
  isEditMode: false,
  isLoading: false,
  typeName: '',
  formType: null,
  isPostRegister: false,
  pages: [],
  pageCountTotal: 0,
  visiblePages: [],
  currentPage: 0,
  validationRequest: false,
  completedForm: null,
  error: null,
  hasChanges: { hasChanges: false },
  paginationVisible: true,
  paginationPreviousPageVisible: true,
  showAllPages: false,
  lastValidatedPage: -1,
  shouldStopBeforeSaving: false,
  stoppedBeforeSaving: false,
  shouldForceSave: false,
  onNextPage: function (currentPage: number): number {
    return currentPage + 1;
  },
  onPreviousPage: function (currentPage: number): number {
    return currentPage - 1;
  },
  additionalQuestionsToConvert: [],
  shouldStopBeforeDrafting: false,
  stoppedBeforeDrafting: false,
  shouldForceDraft: false,
  shouldStopBeforeUpdating: false,
  stoppedBeforeUpdating: false,
  shouldForceUpdate: false,
  isPerformView: false,
  isCareProvider: true,
  questionsToValidate: null,
  isPrinting: false,
  shouldStopBeforeNextPage: false,
  nextPageButtonClicked: false,
  saveButtonClicked: false,
  saveDraftCompleted: { isCompleted: false },
  saveDraftFailed: { isFailed: false },
  shouldFocusFormName: { shouldFocus: false, shouldReadInstructions: false },
  journalizationDisabled: { isDisabled: false },
  nextPageLabel: '',
  customErrorMessages: []
};

const reducer = createReducer<StaticFormState>(
  initialState,
  on(FormActions.reset, (): StaticFormState => {
    return {
      ...initialState
    };
  }),
  on(FormActions.initialize, (state, action): StaticFormState => {
    return {
      ...state,
      pages: action.pages,
      pageCountTotal: action.pages.length,
      visiblePages: action.pages.map((_, index) => index + 1), // Set all pages as visible by default
      currentPage: 1
    };
  }),
  on(FormActions.exitForm, (state): StaticFormState => {
    return {
      ...state
    };
  }),
  on(FormActions.saveForm, (state): StaticFormState => {
    return {
      ...state,
      isLoading: true,
      error: null
    };
  }),
  on(FormActions.saveFormCompleted, (state): StaticFormState => {
    return {
      ...state,
      isLoading: false,
      error: null
    };
  }),
  on(FormActions.saveFormFailed, (state, { error }): StaticFormState => {
    return {
      ...state,
      isLoading: false,
      error: error
    };
  }),
  on(FormActions.saveDraft, (state): StaticFormState => {
    return {
      ...state,
      isLoading: true,
      error: null
    };
  }),
  on(FormActions.saveDraftCompleted, (state): StaticFormState => {
    return {
      ...state,
      isLoading: false,
      saveDraftCompleted: { isCompleted: true },
      error: null
    };
  }),
  on(FormActions.saveDraftFailed, (state, { error }): StaticFormState => {
    return {
      ...state,
      isLoading: false,
      saveDraftFailed: { isFailed: true },
      error: error
    };
  }),
  on(FormActions.cancelForm, (state): StaticFormState => {
    return {
      ...state,
      isLoading: true,
      error: null
    };
  }),
  on(FormActions.cancelFormCompleted, (state): StaticFormState => {
    return {
      ...state,
      isLoading: false,
      error: null
    };
  }),
  on(FormActions.cancelFormFailed, (state, { error }): StaticFormState => {
    return {
      ...state,
      isLoading: false,
      error: error
    };
  }),
  on(FormActions.updateForm, (state): StaticFormState => {
    return {
      ...state,
      isLoading: true,
      error: null
    };
  }),
  on(FormActions.updateFormCompleted, (state): StaticFormState => {
    return {
      ...state,
      isEditMode: false,
      hasChanges: { hasChanges: false },
      error: null
    };
  }),
  on(FormActions.updateFormFailed, (state, { error }): StaticFormState => {
    return {
      ...state,
      isLoading: false,
      error: error
    };
  }),
  on(FormActions.getCompletedForm, (state): StaticFormState => {
    return {
      ...state,
      isLoading: true,
      error: null
    };
  }),
  on(FormActions.getCompletedFormCompleted, (state, { completedForm }): StaticFormState => {
    return {
      ...state,
      completedForm: completedForm,
      isLoading: false,
      error: null
    };
  }),
  on(FormActions.getCompletedFormFailed, (state, { error }): StaticFormState => {
    return {
      ...state,
      isLoading: false,
      error: error
    };
  }),
  on(FormActions.clearCompletedForm, (state): StaticFormState => {
    return {
      ...state,
      completedForm: null
    };
  }),
  on(FormActions.setIsLoading, (state, action): StaticFormState => {
    return {
      ...state,
      isLoading: action.isLoading
    };
  }),
  on(FormActions.setIsEditMode, (state, action): StaticFormState => {
    return {
      ...state,
      isEditMode: action.isEditMode
    };
  }),
  on(FormActions.setTypeName, (state, action): StaticFormState => {
    return {
      ...state,
      typeName: action.typeName
    };
  }),
  on(FormActions.setFormType, (state, action): StaticFormState => {
    return {
      ...state,
      formType: action.formType
    };
  }),
  on(FormActions.setCurrentPage, (state, action): StaticFormState => {
    return {
      ...state,
      currentPage: action.currentPage
    };
  }),
  on(FormActions.setErrorCount, (state, action): StaticFormState => {
    return {
      ...state,
      errorCount: action.errors
    };
  }),
  on(FormActions.incrementErrorCount, (state, action): StaticFormState => {
    return {
      ...state,
      errorCount: state.errorCount + action.incrementBy
    };
  }),
  on(FormActions.requestPageValidation, (state, action): StaticFormState => {
    return {
      ...state,
      validationRequest: true,
      errorCount: 0,
      questionsToValidate: action.questionsForValidationIds
    };
  }),
  on(FormActions.completePageValidation, (state): StaticFormState => {
    return {
      ...state,
      validationRequest: false
    };
  }),
  on(FormActions.setLastValidatedPage, (state, action): StaticFormState => {
    return {
      ...state,
      lastValidatedPage: action.pageNumber
    };
  }),
  on(FormActions.setFormHasChanges, (state, action): StaticFormState => {
    return {
      ...state,
      hasChanges: { hasChanges: action.hasChanges }
    };
  }),
  on(FormActions.setIsPostRegister, (state, action): StaticFormState => {
    return {
      ...state,
      isPostRegister: action.isPostRegister
    };
  }),
  on(FormActions.setPaginationVisibility, (state, action): StaticFormState => {
    return {
      ...state,
      paginationVisible: action.paginationVisible
    };
  }),
  on(FormActions.setVisiblePages, (state, action): StaticFormState => {
    return {
      ...state,
      visiblePages: action.visiblePages
    };
  }),
  on(FormActions.setPageCountTotal, (state, action): StaticFormState => {
    return {
      ...state,
      pageCountTotal: action.pageCount
    };
  }),
  on(FormActions.setShowAllPages, (state): StaticFormState => {
    return {
      ...state,
      currentPage: state.pageCountTotal,
      showAllPages: true
    };
  }),
  on(FormActions.setIsPerformView, (state, action): StaticFormState => {
    return {
      ...state,
      isPerformView: action.isPerformView
    };
  }),
  on(FormActions.setIsCareProvider, (state, action): StaticFormState => {
    return {
      ...state,
      isCareProvider: action.isCareProvider
    };
  }),
  on(FormActions.setShouldStopBeforeSaving, (state, action): StaticFormState => {
    return {
      ...state,
      shouldStopBeforeSaving: action.shouldStopBeforeSaving
    };
  }),
  on(FormActions.setStoppedBeforeSaving, (state, action): StaticFormState => {
    return {
      ...state,
      stoppedBeforeSaving: action.stoppedBeforeSaving
    };
  }),
  on(FormActions.setShouldForceSave, (state, action): StaticFormState => {
    return {
      ...state,
      shouldForceSave: action.shouldForceSave
    };
  }),
  on(FormActions.setOnNextPage, (state, action): StaticFormState => {
    return {
      ...state,
      onNextPage: action.onNextPage
    };
  }),
  on(FormActions.setOnPreviousPage, (state, action): StaticFormState => {
    return {
      ...state,
      onPreviousPage: action.onPreviousPage
    };
  }),
  on(FormActions.setAdditionalQuestionsToConvert, (state, action): StaticFormState => {
    return {
      ...state,
      additionalQuestionsToConvert: action.additionalQuestionsToConvert
    };
  }),
  on(FormActions.setShouldStopBeforeDrafting, (state, action): StaticFormState => {
    return {
      ...state,
      shouldStopBeforeDrafting: action.shouldStopBeforeDrafting
    };
  }),
  on(FormActions.setStoppedBeforeDrafting, (state, action): StaticFormState => {
    return {
      ...state,
      stoppedBeforeDrafting: action.stoppedBeforeDrafting
    };
  }),
  on(FormActions.setShouldForceDraft, (state, action): StaticFormState => {
    return {
      ...state,
      shouldForceDraft: action.shouldForceDraft
    };
  }),
  on(FormActions.setShouldStopBeforeUpdating, (state, action): StaticFormState => {
    return {
      ...state,
      shouldStopBeforeUpdating: action.shouldStopBeforeUpdating
    };
  }),
  on(FormActions.setStoppedBeforeUpdating, (state, action): StaticFormState => {
    return {
      ...state,
      stoppedBeforeUpdating: action.stoppedBeforeUpdating
    };
  }),
  on(FormActions.setShouldForceUpdate, (state, action): StaticFormState => {
    return {
      ...state,
      shouldForceUpdate: action.shouldForceUpdate
    };
  }),
  on(FormActions.setIsPrinting, (state, action): StaticFormState => {
    return {
      ...state,
      isPrinting: action.isPrinting
    };
  }),
  on(FormActions.setShouldStopBeforeNextPage, (state, action): StaticFormState => {
    return {
      ...state,
      shouldStopBeforeNextPage: action.shouldStopBeforeNextPage
    };
  }),
  on(FormActions.setNextPageButtonClicked, (state, action): StaticFormState => {
    return {
      ...state,
      nextPageButtonClicked: action.nextPageButtonClicked
    };
  }),
  on(FormActions.setSaveButtonClicked, (state, action): StaticFormState => {
    return {
      ...state,
      saveButtonClicked: action.saveButtonClicked
    };
  }),
  on(FormActions.setShouldFocusFormName, (state, action): StaticFormState => {
    return {
      ...state,
      shouldFocusFormName: {
        shouldFocus: action.shouldFocusFormName,
        shouldReadInstructions: action.shouldReadInstructions
      }
    };
  }),
  on(FormActions.setJournalizationDisabled, (state, action): StaticFormState => {
    return {
      ...state,
      journalizationDisabled: { isDisabled: action.journalizationDisabled }
    };
  }),
  on(FormActions.setNextPageLabel, (state, action): StaticFormState => {
    return {
      ...state,
      nextPageLabel: action.nextPageLabel
    };
  }),
  on(FormActions.setCustomErrorMessages, (state, action): StaticFormState => {
    return {
      ...state,
      customErrorMessages: action.customErrorMessages
    };
  })
);

export const staticFormFeatureKey = 'staticForm';

export function staticFormReducer(state, action: Action) {
  return reducer(state, action);
}
